import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Service, PaymentCompletionResult } from '../../types';
import { getDefaultPaymentMethods, createPaymentIntent } from '../../utils/paymentUtils';
import ModalWrapper from '../UI/ModalWrapper';
import { T, rgba } from '../../utils/adminTheme';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ?? '');

// ─── Stripe Card-Formular ─────────────────────────────────────────────────────
interface CardFormProps {
  amount: number;
  bookingId: number;
  serviceId: number;
  onSuccess: () => void;
  onBack: () => void;
}

const CardForm: React.FC<CardFormProps> = ({ amount, bookingId, serviceId, onSuccess, onBack }) => {
  const stripe   = useStripe();
  const elements = useElements();
  const [busy,    setBusy]    = useState(false);
  const [errMsg,  setErrMsg]  = useState('');

  const handlePay = async () => {
    if (!stripe || !elements) return;
    setBusy(true);
    setErrMsg('');

    try {
      const { clientSecret } = await createPaymentIntent({
        booking_id:  bookingId,
        customer_id: 0,
        service_id:  serviceId,
        amount,
        currency:    'CHF',
        description: 'Anzahlung',
        method:      'stripe',
        isPrePayment: true,
      });

      const card = elements.getElement(CardElement);
      if (!card) throw new Error('Kein Kartenformular gefunden');

      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card },
      });

      if (error) {
        setErrMsg(error.message ?? 'Zahlung fehlgeschlagen');
      } else if (paymentIntent?.status === 'succeeded') {
        onSuccess();
      }
    } catch (e: any) {
      setErrMsg(e.message ?? 'Unbekannter Fehler');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{
        border: `1px solid ${T.line}`, borderRadius: T.r.md,
        padding: '12px 14px', background: T.surface2,
      }}>
        <CardElement options={{
          style: {
            base: {
              fontFamily: T.body,
              fontSize: '14px',
              color: T.text,
              '::placeholder': { color: T.faint },
            },
            invalid: { color: '#dc2626' },
          },
          hidePostalCode: true,
        }} />
      </div>

      {errMsg && (
        <p style={{ margin: 0, fontSize: 13, color: '#dc2626', fontFamily: T.body }}>{errMsg}</p>
      )}

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button onClick={onBack} disabled={busy} style={{
          padding: '9px 18px', background: T.surface2, border: `1px solid ${T.line}`,
          borderRadius: T.r.md, fontFamily: T.body, fontSize: 14, color: T.muted, cursor: 'pointer',
        }}>
          Zurück
        </button>
        <button onClick={handlePay} disabled={busy || !stripe} style={{
          padding: '9px 20px', background: busy ? T.faint : T.accent,
          border: 'none', borderRadius: T.r.md, fontFamily: T.body,
          fontSize: 14, fontWeight: 600, color: busy ? T.muted : '#fff',
          cursor: busy ? 'not-allowed' : 'pointer', transition: 'background .15s',
        }}>
          {busy ? 'Verarbeite…' : `CHF ${amount.toFixed(2)} bezahlen`}
        </button>
      </div>
    </div>
  );
};

// ─── Haupt-Modal ──────────────────────────────────────────────────────────────
interface PaymentModalProps {
  show:       boolean;
  service:    Service;
  bookingId?: number;
  onClose:    () => void;
  onComplete: (result: PaymentCompletionResult) => void;
}

const PaymentModal: React.FC<PaymentModalProps> = ({ show, service, bookingId = 0, onClose, onComplete }) => {
  if (!show) return null;

  const methods = getDefaultPaymentMethods();
  const [selected,     setSelected]     = useState<string | null>(null);
  const [paymentOption, setPaymentOption] = useState<'partial' | 'full'>('partial');
  const [step,         setStep]         = useState<'select' | 'card' | 'done'>('select');

  const prepaymentRequired = service.prepayment;
  const prepaymentAmount   = service.prepayment_amount ?? service.prepaymentAmount ?? 0;
  const totalAmount        = service.price;
  const payAmount          = prepaymentRequired && paymentOption === 'partial' ? prepaymentAmount : totalAmount;

  const handleNext = () => {
    if (!selected) return;
    if (selected === 'cash') {
      onComplete({ method: 'cash', amount: payAmount, isPrepayment: false });
      onClose();
      return;
    }
    setStep('card');
  };

  const handleCardSuccess = () => {
    setStep('done');
    onComplete({ method: 'stripe', amount: payAmount, isPrepayment: prepaymentRequired && paymentOption === 'partial' });
    setTimeout(onClose, 1500);
  };

  return (
    <ModalWrapper onClose={onClose} maxWidth={480}>
      <h2 style={{ margin: '0 0 20px', fontFamily: T.display, fontStyle: 'italic', fontSize: 22, fontWeight: 500, color: T.text }}>
        Zahlung
      </h2>

      {/* Buchungsübersicht */}
      <div style={{ background: T.surface2, border: `1px solid ${T.line}`, borderRadius: T.r.md, padding: '12px 16px', marginBottom: 20, display: 'grid', gap: 8 }}>
        {([
          ['Behandlung',  service.name],
          ['Gesamtpreis', `CHF ${totalAmount.toFixed(2)}`],
          ...(prepaymentRequired ? [['Anzahlung', `CHF ${prepaymentAmount.toFixed(2)}`]] : []),
        ] as [string, string][]).map(([k, v]) => (
          <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
            <span style={{ color: T.muted, fontFamily: T.body }}>{k}</span>
            <span style={{ color: T.text, fontWeight: 600, fontFamily: T.body }}>{v}</span>
          </div>
        ))}
      </div>

      {/* ─── Schritt 1: Auswahl ─── */}
      {step === 'select' && (
        <>
          {prepaymentRequired && (
            <div style={{ marginBottom: 20 }}>
              <p style={{ margin: '0 0 10px', fontFamily: T.body, fontSize: 12, fontWeight: 600, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Zahlungsoption</p>
              <div style={{ display: 'grid', gap: 8 }}>
                {(['partial', 'full'] as const).map(opt => (
                  <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: T.body, fontSize: 14, color: T.text, cursor: 'pointer' }}>
                    <input type="radio" checked={paymentOption === opt} onChange={() => setPaymentOption(opt)} style={{ accentColor: T.accent }} />
                    {opt === 'partial' ? 'Anzahlung jetzt, Rest bar vor Ort' : 'Gesamtbetrag jetzt online bezahlen'}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginBottom: 24 }}>
            <p style={{ margin: '0 0 10px', fontFamily: T.body, fontSize: 12, fontWeight: 600, color: T.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Zahlungsmethode</p>
            <div style={{ display: 'grid', gap: 8 }}>
              {methods.map(m => {
                const active = selected === m.id;
                return (
                  <button key={m.id} onClick={() => setSelected(m.id)} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px',
                    background: active ? rgba(T.accent, 0.06) : T.surface2,
                    border: `1px solid ${active ? T.accent : T.line}`,
                    borderRadius: T.r.md, cursor: 'pointer', textAlign: 'left', transition: 'border .15s, background .15s',
                  }}>
                    <span style={{ fontSize: 22 }}>{m.icon}</span>
                    <div>
                      <div style={{ fontFamily: T.body, fontSize: 14, fontWeight: 600, color: T.text }}>{m.name}</div>
                      <div style={{ fontFamily: T.body, fontSize: 12, color: T.muted }}>{m.description}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button onClick={onClose} style={{ padding: '9px 18px', background: T.surface2, border: `1px solid ${T.line}`, borderRadius: T.r.md, fontFamily: T.body, fontSize: 14, color: T.muted, cursor: 'pointer' }}>
              Abbrechen
            </button>
            <button onClick={handleNext} disabled={!selected} style={{
              padding: '9px 20px', background: selected ? T.accent : T.faint,
              border: 'none', borderRadius: T.r.md, fontFamily: T.body,
              fontSize: 14, fontWeight: 600, color: selected ? '#fff' : T.muted,
              cursor: selected ? 'pointer' : 'not-allowed', transition: 'background .15s',
            }}>
              {selected === 'cash' ? 'Bestätigen' : `Weiter → CHF ${payAmount.toFixed(2)}`}
            </button>
          </div>
        </>
      )}

      {/* ─── Schritt 2: Stripe Card ─── */}
      {step === 'card' && (
        <Elements stripe={stripePromise}>
          <CardForm
            amount={payAmount}
            bookingId={bookingId}
            serviceId={service.id}
            onSuccess={handleCardSuccess}
            onBack={() => setStep('select')}
          />
        </Elements>
      )}

      {/* ─── Schritt 3: Erfolg ─── */}
      {step === 'done' && (
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
          <div style={{ fontFamily: T.display, fontStyle: 'italic', fontSize: 20, color: T.text }}>
            Zahlung erfolgreich
          </div>
          <div style={{ fontFamily: T.body, fontSize: 13, color: T.muted, marginTop: 6 }}>
            CHF {payAmount.toFixed(2)} erhalten
          </div>
        </div>
      )}
    </ModalWrapper>
  );
};

export default PaymentModal;
